/** CSS tokens for user-ht-gold themes — layout is independent of user-htd. */

export type HtGoldThemeMode = 'dark' | 'light'

export interface HtGoldThemeTokens {
  mode: HtGoldThemeMode
  primary: string
  primaryLight: string
  secondary: string
  dark: string
  onPrimary: string
  success: string
  danger: string
  text: string
  textMuted: string
}

const DARK_BASE = {
  success: '#27a945',
  danger: '#f03939',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.72)',
} as const

const LIGHT_BASE = {
  success: '#1f9a3a',
  danger: '#e03535',
  text: '#142033',
  textMuted: 'rgba(20, 32, 51, 0.62)',
} as const

/** Original 10 — dark navy / black mix */
export const HT_GOLD_DARK_THEMES: Record<string, HtGoldThemeTokens> = {
  'gold-navy': {
    mode: 'dark',
    primary: '#ffaf00',
    primaryLight: '#ffdf9a',
    secondary: '#1a5d94',
    dark: '#142143',
    onPrimary: '#0b1224',
    ...DARK_BASE,
  },
  'amber-royal': {
    mode: 'dark',
    primary: '#f5a623',
    primaryLight: '#ffe3a3',
    secondary: '#0f3d6e',
    dark: '#0a1833',
    onPrimary: '#0b1224',
    ...DARK_BASE,
  },
  'copper-navy': {
    mode: 'dark',
    primary: '#ff6400',
    primaryLight: '#ffc08a',
    secondary: '#1a4f7a',
    dark: '#101c38',
    onPrimary: '#140a04',
    ...DARK_BASE,
  },
  'emerald-gold': {
    mode: 'dark',
    primary: '#ffb000',
    primaryLight: '#ffe29a',
    secondary: '#0e6b5a',
    dark: '#062e2a',
    onPrimary: '#0b1224',
    ...DARK_BASE,
  },
  'ruby-gold': {
    mode: 'dark',
    primary: '#ffc107',
    primaryLight: '#ffe9a8',
    secondary: '#7a1f3d',
    dark: '#2a0d1c',
    onPrimary: '#1a0a08',
    ...DARK_BASE,
  },
  'violet-gold': {
    mode: 'dark',
    primary: '#ffaf00',
    primaryLight: '#ffdf9a',
    secondary: '#5a1a8f',
    dark: '#1e0a3a',
    onPrimary: '#0b1224',
    ...DARK_BASE,
  },
  'ocean-pearl': {
    mode: 'dark',
    primary: '#e8c36a',
    primaryLight: '#f7e7b8',
    secondary: '#2a7fb8',
    dark: '#12324f',
    onPrimary: '#1a1408',
    ...DARK_BASE,
  },
  'midnight-cyan': {
    mode: 'dark',
    primary: '#22d3ee',
    primaryLight: '#a5f3fc',
    secondary: '#1e3a8a',
    dark: '#0b1224',
    onPrimary: '#042f2e',
    ...DARK_BASE,
  },
  'slate-champagne': {
    mode: 'dark',
    primary: '#d4b483',
    primaryLight: '#f0e1c4',
    secondary: '#3d4f66',
    dark: '#1c2430',
    onPrimary: '#1a140c',
    ...DARK_BASE,
  },
  'forest-honey': {
    mode: 'dark',
    primary: '#f0b429',
    primaryLight: '#ffe6a0',
    secondary: '#2f6b3a',
    dark: '#142616',
    onPrimary: '#1a1204',
    ...DARK_BASE,
  },
}

/**
 * Same 10 hues mixed toward white (light mode).
 * `dark` = near-white page base · `secondary` = soft tint of the original secondary.
 */
export const HT_GOLD_WHITE_THEMES: Record<string, HtGoldThemeTokens> = {
  'gold-navy-white': {
    mode: 'light',
    primary: '#e89f00',
    primaryLight: '#ffe7b0',
    secondary: '#c5daf0',
    dark: '#ffffff',
    onPrimary: '#0b1224',
    ...LIGHT_BASE,
  },
  'amber-royal-white': {
    mode: 'light',
    primary: '#e09510',
    primaryLight: '#ffe8b5',
    secondary: '#c2d4ea',
    dark: '#ffffff',
    onPrimary: '#0b1224',
    ...LIGHT_BASE,
  },
  'copper-navy-white': {
    mode: 'light',
    primary: '#ef5c00',
    primaryLight: '#ffd0a0',
    secondary: '#c6d8ea',
    dark: '#ffffff',
    onPrimary: '#140a04',
    ...LIGHT_BASE,
  },
  'emerald-gold-white': {
    mode: 'light',
    primary: '#e6a000',
    primaryLight: '#ffe7ad',
    secondary: '#c5e6de',
    dark: '#ffffff',
    onPrimary: '#0b1224',
    ...LIGHT_BASE,
  },
  'ruby-gold-white': {
    mode: 'light',
    primary: '#e8b000',
    primaryLight: '#ffefb8',
    secondary: '#efd0da',
    dark: '#ffffff',
    onPrimary: '#1a0a08',
    ...LIGHT_BASE,
  },
  'violet-gold-white': {
    mode: 'light',
    primary: '#e89f00',
    primaryLight: '#ffe7b0',
    secondary: '#e0d0f0',
    dark: '#ffffff',
    onPrimary: '#0b1224',
    ...LIGHT_BASE,
  },
  'ocean-pearl-white': {
    mode: 'light',
    primary: '#d4ad4a',
    primaryLight: '#f7e7b8',
    secondary: '#cce4f4',
    dark: '#ffffff',
    onPrimary: '#1a1408',
    ...LIGHT_BASE,
  },
  'midnight-cyan-white': {
    mode: 'light',
    primary: '#0891b2',
    primaryLight: '#a5f3fc',
    secondary: '#cfe4fb',
    dark: '#ffffff',
    onPrimary: '#042f2e',
    ...LIGHT_BASE,
  },
  'slate-champagne-white': {
    mode: 'light',
    primary: '#c49a5e',
    primaryLight: '#f0e1c4',
    secondary: '#dce2e9',
    dark: '#ffffff',
    onPrimary: '#1a140c',
    ...LIGHT_BASE,
  },
  'forest-honey-white': {
    mode: 'light',
    primary: '#d9a012',
    primaryLight: '#ffe6a0',
    secondary: '#d5e8d8',
    dark: '#ffffff',
    onPrimary: '#1a1204',
    ...LIGHT_BASE,
  },
}

export const HT_GOLD_THEMES: Record<string, HtGoldThemeTokens> = {
  ...HT_GOLD_DARK_THEMES,
  ...HT_GOLD_WHITE_THEMES,
}

export const DEFAULT_HT_GOLD_THEME_ID = 'gold-navy'

export function applyHtGoldCssVars(themeId: string): void {
  const t = HT_GOLD_THEMES[themeId] || HT_GOLD_THEMES[DEFAULT_HT_GOLD_THEME_ID]
  const root = document.documentElement
  const resolvedId = themeId in HT_GOLD_THEMES ? themeId : DEFAULT_HT_GOLD_THEME_ID
  root.dataset.htGoldTheme = resolvedId
  root.dataset.htGoldMode = t.mode
  root.style.setProperty('--htg-primary', t.primary)
  root.style.setProperty('--htg-primary-light', t.primaryLight)
  root.style.setProperty('--htg-secondary', t.secondary)
  root.style.setProperty('--htg-dark', t.dark)
  root.style.setProperty('--htg-on-primary', t.onPrimary)
  root.style.setProperty('--htg-success', t.success)
  root.style.setProperty('--htg-danger', t.danger)
  root.style.setProperty('--htg-text', t.text)
  root.style.setProperty('--htg-text-muted', t.textMuted)
  root.style.setProperty(
    '--htg-border',
    `color-mix(in srgb, ${t.primary} 35%, transparent)`
  )
  root.style.setProperty(
    '--htg-surface',
    t.mode === 'light'
      ? `color-mix(in srgb, ${t.secondary} 55%, #ffffff)`
      : `color-mix(in srgb, ${t.dark} 72%, transparent)`
  )
  root.style.setProperty(
    '--htg-shadow',
    t.mode === 'light' ? '0 12px 28px rgba(15, 23, 42, 0.12)' : '0 12px 32px rgba(0, 0, 0, 0.35)'
  )
}
